import { useEffect, useRef } from "preact/hooks";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { SkeletonLoader } from "../SkeletonLoader";
import { ErrorMessage } from "../ErrorMessage";
import {
  getChartColors,
  isoToUnixSeconds,
  createTooltip,
  makeTimeFormatter,
  useChartTheme,
} from "./chartUtils";

type SignalChartProps = {
  data: Array<{
    timestamp: string;
    tx_signal: number;
    rx_signal: number;
  }>;
  loading?: boolean;
  error?: string;
};

function formatDbmAxis(_u: uPlot, splits: number[]): string[] {
  return splits.map((v) => `${v} dBm`);
}

export function SignalChart({ data, loading, error }: SignalChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const theme = useChartTheme();

  useEffect(() => {
    if (loading || error || data.length === 0) return;
    if (!containerRef.current) return;

    const colors = getChartColors();
    const tooltip = createTooltip();
    tooltipRef.current = tooltip;
    containerRef.current.appendChild(tooltip);

    const timestamps = data.map((d) => isoToUnixSeconds(d.timestamp));
    const tx = data.map((d) => d.tx_signal);
    const rx = data.map((d) => d.rx_signal);

    const allValues = [...tx, ...rx];
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const yMin = Math.min(minValue, -90) - 5;
    const yMax = Math.max(maxValue, -30) + 5;

    const width = containerRef.current.clientWidth;
    const height = 300;

    const opts: uPlot.Options = {
      width,
      height,
      cursor: { drag: { x: false, y: false } },
      legend: { show: true },
      scales: {
        y: {
          range: [yMin, yMax],
        },
      },
      axes: [
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: makeTimeFormatter(timestamps),
        },
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: formatDbmAxis,
          size: 60,
        },
      ],
      series: [
        {},
        {
          label: "TX Signal",
          stroke: colors.blue,
          width: 2,
          points: { show: true, size: 4, stroke: colors.blue, fill: colors.blue },
        },
        {
          label: "RX Signal",
          stroke: colors.green,
          width: 2,
          points: { show: true, size: 4, stroke: colors.green, fill: colors.green },
        },
      ],
      hooks: {
        setCursor: [
          (u) => {
            const idx = u.cursor.idx;
            if (idx == null || idx < 0) {
              tooltip.style.display = "none";
              return;
            }
            const t = u.data[0][idx];
            const txVal = u.data[1][idx];
            const rxVal = u.data[2][idx];
            if (t == null || txVal == null || rxVal == null) {
              tooltip.style.display = "none";
              return;
            }
            tooltip.innerHTML = `
              <div><strong>${new Date(t * 1000).toLocaleString("id-ID")}</strong></div>
              <div style="color:${colors.blue}">TX: ${txVal} dBm</div>
              <div style="color:${colors.green}">RX: ${rxVal} dBm</div>
            `;
            tooltip.style.display = "block";
            tooltip.style.left = `${(u.cursor.left ?? 0) + 12}px`;
            tooltip.style.top = `${(u.cursor.top ?? 0) + 12}px`;
          },
        ],
      },
    };

    const chart = new uPlot(opts, [timestamps, tx, rx], containerRef.current);
    chartRef.current = chart;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chart.setSize({ width: entry.contentRect.width, height });
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.destroy();
      tooltip.remove();
      chartRef.current = null;
      tooltipRef.current = null;
    };
  }, [data, theme, loading, error]);

  if (loading) return <SkeletonLoader type="chart" />;
  if (error) return <ErrorMessage message={error} />;
  if (data.length === 0) return <div class="chart-empty">Belum ada data</div>;

  return <div ref={containerRef} class="chart-container" />;
}
